import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { DistanceUnit, VolumeUnit } from 'shared/src/model/common'
import { isValue } from 'shared/src/util/typeGuard'
import { CommonUrlQueryKeys } from 'shared/src/util/url'

export enum UnitSystem {
  METRIC = 'metric',
  US_CUSTOMARY = 'us',
}

export type SelectedUnitsType = {
  distance: DistanceUnit
  volume: VolumeUnit
}

type MeasurementContextType = {
  currentUnitSystemType: UnitSystem
  selectedUnits: SelectedUnitsType
  setUnitSystem: (unitSystem: UnitSystem) => void
}

const MeasurementContext = createContext<MeasurementContextType | undefined>(undefined)

const MeasurementProvider = ({ children }: PropsWithChildren) => {
  const unitSystemFromUrl = new URLSearchParams(window.location.search).get(CommonUrlQueryKeys.UNIT_SYSTEM)
  const [currentUnitSystem, setCurrentUnitSystem] = useState<UnitSystem>(
    (unitSystemFromUrl as UnitSystem) ?? UnitSystem.METRIC,
  )

  const setUnitSystem = useCallback((unitSystem: UnitSystem) => {
    const url = new URL(window.location.href)
    url.searchParams.set(CommonUrlQueryKeys.UNIT_SYSTEM, unitSystem)
    window.history.pushState({}, '', url.toString())
    setCurrentUnitSystem(unitSystem)
  }, [])

  const value = useMemo(
    () => ({
      currentUnitSystemType: currentUnitSystem,
      selectedUnits: {
        distance: currentUnitSystem === UnitSystem.US_CUSTOMARY ? DistanceUnit.INCH : DistanceUnit.CENTIMETER,
        volume: currentUnitSystem === UnitSystem.US_CUSTOMARY ? VolumeUnit.GALLON_LIQUID_US : VolumeUnit.LITER,
      },
      setUnitSystem,
    }),
    [currentUnitSystem, setUnitSystem],
  )

  return <MeasurementContext.Provider value={value}>{children}</MeasurementContext.Provider>
}

const useMeasurementContext = () => {
  const context = useContext(MeasurementContext)

  if (!isValue(context)) {
    throw new Error('The hook useMeasurementContext must be used inside a MeasurementProvider')
  }
  return context
}

export { MeasurementProvider, useMeasurementContext }
